import { useEffect, useMemo, useRef, useState } from 'react'
import { ActionIcon, Alert, Group, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { usePreviewBridge } from '../../hooks/usePreviewBridge'
import { PreviewStatus } from './PreviewStatus'
import classes from '../../styles/StudioEditorV2.module.css'

// This component always frames the real package runtime; it never imitates the ad in React.

/** The mock publisher chrome the Figma preview places around the ad. */
function DeviceHeader() {
  return (
    <div className={classes.deviceHeader} aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="8" cy="8" r="5.2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 12l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <span className={classes.deviceBrand}>
        LOCAL<span className={classes.deviceBrandMark}>NEWS</span>
      </span>
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3.5 15.5c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </div>
  )
}

export function LivePreview({
  previewKey,
  previewUrl,
  configuration,
  selectedSlide,
  onSelectSlide,
  selectedItems,
  onSelectItem,
  protocolVersion,
  onStatusChange,
}) {
  const { t } = useTranslation()
  const iframeRef = useRef(null)
  const measureRef = useRef(null)
  const frameRef = useRef(null)
  const [availableWidth, setAvailableWidth] = useState(0)
  const [deviceFit, setDeviceFit] = useState({ scale: 1, height: null })
  const dimensions = configuration?.format || { width: 300, height: 250 }
  const src = useMemo(() => previewUrl || null, [previewUrl])
  const bridge = usePreviewBridge({
    iframeRef,
    templateId: previewKey,
    configuration,
    selectedSlide,
    onSelectSlide,
    selectedItems,
    onSelectItem,
    protocolVersion,
  })

  useEffect(() => {
    onStatusChange?.({ status: bridge.status, error: bridge.error, diagnostics: bridge.diagnostics, configuration: bridge.diagnosticConfiguration, checked: bridge.diagnosticConfiguration === configuration })
  }, [bridge.error, bridge.status, bridge.diagnostics, bridge.diagnosticConfiguration, configuration, onStatusChange])

  useEffect(() => {
    const element = measureRef.current
    if (!element) return undefined
    const measure = () => setAvailableWidth(element.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const scale = availableWidth
    ? Math.min(1, Math.max(0.1, availableWidth / Number(dimensions.width || 300)))
    : 1
  const scaledHeight = Math.ceil(Number(dimensions.height || 250) * scale)

  // The phone is sticky, so on a small laptop a 300×600 ad made it taller than the
  // viewport and its lower half could never be reached. Shrink the whole device (a
  // transform, so the ad keeps rendering at its true pixel size inside) until it fits
  // under the app header, and give the wrapper the shrunken height so the column and
  // the Ad Agent card follow.
  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return undefined
    const fit = () => {
      const natural = frame.offsetHeight
      if (!natural) return
      const headerOffset = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--studio-header-offset'),
      ) || 70
      const available = window.innerHeight - headerOffset - 18 - 24
      const nextScale = available > 0 ? Math.min(1, Math.max(0.35, available / natural)) : 1
      setDeviceFit((current) => (
        Math.abs(current.scale - nextScale) < 0.005 && current.height === Math.ceil(natural * nextScale)
          ? current
          : { scale: nextScale, height: Math.ceil(natural * nextScale) }
      ))
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(frame)
    window.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
    }
  }, [scaledHeight])

  // Figma shows the phone alone. Status and restart appear only when something is
  // wrong, so the healthy state stays as clean as the design.
  const unhealthy = bridge.status === 'error' || Boolean(bridge.error)

  return (
    <div className={classes.previewColumnInner}>
      {(unhealthy || bridge.status === 'loading') && (
        <Group justify="space-between" align="center" mb="xs">
          <PreviewStatus status={bridge.status} />
        </Group>
      )}

      {bridge.error && (
        <Alert color="red" icon={<IconAlertTriangle size={17} />} title={bridge.error.phase} mb="xs">
          {bridge.error.message}
        </Alert>
      )}

      <div
        className={classes.deviceFit}
        style={deviceFit.scale < 1 && deviceFit.height ? { height: deviceFit.height } : undefined}
      >
      <div
        ref={frameRef}
        className={classes.deviceFrame}
        style={deviceFit.scale < 1 ? { transform: `scale(${deviceFit.scale})` } : undefined}
      >
        {/* Always reachable: interactive templates (scratch-to-reveal) need a way to reset
            the preview after the editor has played with it, not only when it broke. */}
        <Tooltip label={t('editorV2.shell.preview.restart')} withArrow>
          <ActionIcon
            className={classes.deviceRestart}
            variant="default"
            size="md"
            radius="xl"
            aria-label={t('editorV2.shell.preview.restart')}
            disabled={bridge.status === 'loading'}
            onClick={bridge.restart}
          >
            <IconRefresh size={15} />
          </ActionIcon>
        </Tooltip>
        <div className={classes.device}>
          <div className={classes.deviceScreen}>
            <DeviceHeader />
            <span className={classes.skeletonLine} style={{ height: 34, borderRadius: 6 }} />

            <div
              ref={measureRef}
              className={classes.adSlot}
              style={{ height: Math.max(60, scaledHeight) }}
            >
              {src && (
                <div
                  className={classes.previewScale}
                  style={{
                    width: dimensions.width,
                    height: dimensions.height,
                    transform: `scale(${scale})`,
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    src={src}
                    title={t('editorV2.shell.preview.frameTitle')}
                    width={dimensions.width}
                    height={dimensions.height}
                    sandbox="allow-scripts"
                    referrerPolicy="no-referrer"
                    onLoad={bridge.onFrameLoad}
                  />
                </div>
              )}
            </div>

            <span className={classes.skeletonBlock} />
            <span className={classes.skeletonLine} />
            <span className={classes.skeletonLine} />
            <span className={classes.skeletonLine} />
            <div className={classes.deviceHomeBar} aria-hidden="true" />
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
